import React, { useState, useEffect, useMemo, useCallback } from 'react';
import UserForm from './UserForm';
import SectionCard from '../../components/SectionCard';
import { IcPlus, IcUserPlus, IcPencil, IcTrash, IcSearch, IcClose, IcUser, IcUsers, IcShieldCheck, IcUserCheck, IcToggleOn, IcToggleOff, IcMail } from '../../theme/icons';
import Swal from '../../theme/swal';
import authService from '../../services/authService';
import KI from '../../theme/palette';
import { TableSkeleton } from '../../components/Skeleton';
import { matchesSearch } from '../../utils/search';

// Étiquettes Ki Para (Etiquettes-2 §13) : Admin = amber (attention), Caisse =
// gris neutre — rôles discrets, sans icône.
const ROLE_META = {
  admin: { label: 'Propriétaire', badge: 'bdg-warn' },
  manager: { label: 'Manager', badge: 'bdg-pos' },
  seller: { label: 'Caisse', badge: 'bdg-neutral' },
};
const roleMeta = (role) => ROLE_META[role] || { label: role || '—', badge: 'bdg-neutral' };

// `embedded` : rendu dans Paramètres › Utilisateurs (entête compact, sans chip).
const UserList = ({ embedded = false } = {}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | admin | seller | active
  const [busyId, setBusyId] = useState(null);   // user id currently being toggled

  const currentUserId = authService.getCurrentUser()?.user_id;

  // Panneau Nouvel utilisateur / Modifier (owner 2026-09-08) : 'new' | id | null
  const [userForm, setUserForm] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authService.getAllUsers();
      const list = Array.isArray(data) ? data : (data?.results || []);
      setUsers(list);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Impossible de charger les utilisateurs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authService.isOwner()) {
      window.location.href = '/';
      return;
    }
    fetchUsers();
  }, [fetchUsers]);

  const counts = useMemo(() => ({
    total: users.length,
    admins: users.filter((u) => u.role === 'admin').length,
    sellers: users.filter((u) => u.role === 'seller').length,
    active: users.filter((u) => u.is_active).length,
  }), [users]);

  const displayed = useMemo(() => {
    return users.filter((u) => {
      if (filter === 'admin' && u.role !== 'admin') return false;
      if (filter === 'seller' && u.role !== 'seller') return false;
      if (filter === 'active' && !u.is_active) return false;
      // Règle Ki Para (utils/search.js) : nom par début de mot, e-mail « contient ».
      return matchesSearch(search, u.username, [u.email]);
    });
  }, [users, search, filter]);

  const toggleActive = async (user) => {
    setBusyId(user.id);
    try {
      await authService.updateUserStatus(user.id, { is_active: !user.is_active });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, is_active: !u.is_active } : u)));
    } catch (err) {
      console.error('Toggle active failed:', err);
      Swal.fire('Erreur', "Impossible de modifier le statut de l'utilisateur.", 'error');
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (user) => {
    const res = await Swal.fire({
      title: 'Supprimer cet utilisateur ?',
      html: `<b>${user.username}</b><br><span style="color:${KI.faint}">${user.email || ''}</span>`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: KI.coralDark,
      confirmButtonText: 'Oui, supprimer', cancelButtonText: 'Annuler',
    });
    if (!res.isConfirmed) return;
    try {
      await authService.deleteUser(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      Swal.fire({ icon: 'success', title: 'Supprimé', timer: 1400, showConfirmButton: false });
    } catch (err) {
      console.error('Error deleting user:', err);
      Swal.fire('Erreur', err?.detail || "Impossible de supprimer l'utilisateur.", 'error');
    }
  };

  // Carte KPI (Composants-2 §5) = filtre rapide : blanche, bordée + drapeau si actif.
  const StatCard = ({ id, label, value, Icon, sub }) => {
    const active = filter === id && id !== 'all';
    return (
      <button
        type="button"
        onClick={() => setFilter((f) => (f === id ? 'all' : id))}
        className={`text-left kpi rounded-2xl p-5 transition-all ${active ? 'kpi-active' : 'hover:shadow-md'}`}
      >
        {active && <span className="kpi-flag">FILTRE ACTIF</span>}
        <Icon size={16} className="kpi-icon" />
        <p className="kpi-label text-sm uppercase pr-6">{label}</p>
        <p className="kpi-value text-2xl">{value}</p>
        <p className="kpi-sub text-xs mt-1">{active ? 'Filtre actif · cliquer pour tout voir' : sub}</p>
      </button>
    );
  };

  const listContent = loading ? (
        <TableSkeleton rows={6} columns={[
              { label: 'Utilisateur', w: 300 },
              { label: 'Rôle', w: 80 },
              { label: 'Boutique', w: 100 },
              { label: 'Statut', w: 80 },
              { label: 'Actions', align: 'right', w: 60 },
            ]} />
      ) : displayed.length === 0 ? (
        <div className={`empty-ki rounded-2xl p-12 ${embedded ? 'm-6' : ''}`}>
          <IcUsers size={48} className="empty-ki-icon w-12 h-12 mb-4" />
          <p className="empty-ki-title text-lg mt-0">
            {search || filter !== 'all' ? 'Aucun utilisateur correspondant' : 'Aucun utilisateur'}
          </p>
          <p className="empty-ki-sub text-sm mt-1 mb-0">
            {search || filter !== 'all' ? 'Essayez un autre mot, ou retirez le filtre.' : 'Créez le premier compte.'}
          </p>
        </div>
      ) : (
        /* Liste (owner 2026-09-09 : cartes -> tableau dense, Composants-2 §6) :
           avatar N&B + nom + email, rôle, statut, actions à droite. */
        <div className={embedded ? '' : 'bg-white rounded-2xl border border-gray-200 overflow-hidden'}>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th scope="col" className="th-ki px-5 py-3 text-left uppercase">Utilisateur</th>
                  <th scope="col" className="th-ki px-5 py-3 text-left uppercase w-32">Rôle</th>
                  <th scope="col" className="th-ki px-5 py-3 text-left uppercase w-44">Boutique</th>
                  <th scope="col" className="th-ki px-5 py-3 text-left uppercase w-64">Statut</th>
                  <th scope="col" className="th-ki px-5 py-3 text-right uppercase w-56">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((user) => {
                  const meta = roleMeta(user.role);
                  const isSelf = user.id === currentUserId;
                  return (
                    <tr key={user.id} className="border-t border-gray-100 hover:bg-gray-100 transition-colors">
                      <td className="td-ki px-5 py-3 text-sm">
                        <div className="flex items-center gap-3 min-w-0">
                          {/* Avatar de profil (Ahmed 2026-08-16) : noir & blanc, silhouette humaine */}
                          <div className="h-9 w-9 shrink-0 rounded-full bg-gray-100 text-black flex items-center justify-center">
                            <IcUser size={16} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900 leading-tight truncate">{user.username}</p>
                              {isSelf && <span className="bdg bdg-ink">Vous</span>}
                            </div>
                            <p className="text-xs text-gray-400 truncate flex items-center gap-1 mt-0.5"><IcMail className="text-[10px]" /> {user.email || '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="td-ki px-5 py-3 text-sm whitespace-nowrap">
                        <span className={`bdg ${meta.badge}`}>{meta.label}</span>
                      </td>
                      <td className="td-ki px-5 py-3 text-sm whitespace-nowrap text-gray-700">
                        {user.role === 'admin' ? <span className="text-gray-400">Toutes</span> : ((user.store_names || []).join(', ') || <span className="bdg bdg-warn">Sans boutique</span>)}
                      </td>
                      <td className="td-ki px-5 py-3 text-sm whitespace-nowrap">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`bdg ${user.is_active ? 'bdg-pos' : 'bdg-neutral'}`}>
                            {user.is_active ? '● Actif' : '○ Inactif'}
                          </span>
                          {user.role === 'seller' && (
                            <span className={`bdg ${user.has_facturation_access ? 'bdg-pos' : 'bdg-neutral'}`}>
                              {user.has_facturation_access ? 'Accès facturation' : 'Sans facturation'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="td-ki px-5 py-3 text-sm whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button type="button" onClick={() => setUserForm(user.id)} className="btn-g-edit gap-2 px-3 py-1.5 text-sm transition-colors">
                            <IcPencil /> Modifier
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleActive(user)}
                            disabled={isSelf || busyId === user.id}
                            title={isSelf ? 'Vous ne pouvez pas désactiver votre propre compte' : (user.is_active ? 'Désactiver' : 'Activer')}
                            aria-label={user.is_active ? 'Désactiver le compte' : 'Activer le compte'}
                            className={`h-9 w-9 flex items-center justify-center rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${user.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                          >
                            {user.is_active ? <IcToggleOn className="text-lg" /> : <IcToggleOff className="text-lg" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteUser(user)}
                            disabled={isSelf}
                            title={isSelf ? 'Vous ne pouvez pas supprimer votre propre compte' : 'Supprimer'}
                            aria-label="Supprimer le compte"
                            className="h-9 w-9 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <IcTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );

  return (
    <div className={embedded ? 'w-full' : 'w-full px-4 pt-2 pb-4'}>
      {/* En-tête : chip gris (page seule) ou titre de section (Paramètres),
          « Ajouter un utilisateur » btn-add-block à droite. */}
      {/* Paramètres (Studio owner 2026-09-09, même règle que Services) : ni titre,
          ni recherche, ni cartes KPI — les cartes seules, « Ajouter un utilisateur »
          dessous à droite. La page /users seule garde tout. */}
      {!embedded && (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
        <button type="button" onClick={() => window.location.reload()} title="Actualiser la page" className="btn-s px-4 py-2 rounded-xl text-sm self-start">
          <IcUsers size={13} /> Gestion des utilisateurs
        </button>
        <button type="button" onClick={() => setUserForm('new')} className="btn-add-block w-auto px-4 py-2.5 transition-colors self-start sm:self-auto">
          <IcUserPlus className="mr-2" /> Ajouter un utilisateur
        </button>
      </div>
      )}

      {/* Recherche pleine largeur */}
      {!embedded && (
      <div className="relative mb-4">
        <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par nom ou email…"
          className="inp h-12 w-full pl-11 pr-10"
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Effacer la recherche">
            <IcClose />
          </button>
        )}
      </div>
      )}

      {/* Cartes KPI = filtres rapides (page seule) */}
      {!embedded && (
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard id="all" label="Total" value={counts.total} Icon={IcUsers} sub="Tous les comptes" />
        <StatCard id="admin" label="Administrateurs" value={counts.admins} Icon={IcShieldCheck} sub="Cliquer pour filtrer" />
        <StatCard id="seller" label="Caisses" value={counts.sellers} Icon={IcUserCheck} sub="Cliquer pour filtrer" />
        <StatCard id="active" label="Actifs" value={counts.active} Icon={IcToggleOn} sub="Cliquer pour filtrer" />
      </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4" role="alert">{error}</div>
      )}

      {/* Paramètres : même cadre que Tarification (owner 2026-09-09). */}
      {embedded ? (
        <SectionCard
          title="Comptes"
          sub={`${counts.total} compte(s) — ce qui se connecte sur un écran. Les personnes qui s’en servent : onglet Personnes.`}
          flush
          action={!loading && (
            <button
              type="button"
              onClick={() => setUserForm('new')}
              className="h-9 w-9 flex items-center justify-center btn-mini transition-colors shrink-0"
              title="Ajouter un compte"
              aria-label="Ajouter un compte"
            >
              <IcPlus />
            </button>
          )}
        >
          {listContent}
        </SectionCard>
      ) : listContent}

      {/* Panneau Nouvel utilisateur / Modifier — même patron de modale que les
          catégories (entête encre, corps = UserForm en mode panneau). */}
      {userForm !== null && (
        <div className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) setUserForm(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden">
            <div className="modal-head p-5">
              <h3 className="modal-title text-lg">
                {userForm === 'new' ? 'Nouveau compte' : 'Modifier le compte'}
              </h3>
              <button type="button" onClick={() => setUserForm(null)} className="text-gray-400 hover:text-white" aria-label="Fermer">
                <IcClose size={18} />
              </button>
            </div>
            <UserForm
              key={userForm}
              userId={userForm === 'new' ? undefined : userForm}
              onDone={() => { setUserForm(null); fetchUsers(); }}
              onCancel={() => setUserForm(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
